/* React */
import { CSSProperties, useMemo, useState } from 'react'

/* Redux */
import { useDispatch } from 'src/redux/hooks'

/* Next */
import Link from 'src/components/Link'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemButton from '@mui/material/ListItemButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import PinterestIcon from '@mui/icons-material/Pinterest'
import YouTubeIcon from '@mui/icons-material/YouTube'

import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import parse from 'html-react-parser'

/* Types */
import {
  MenuPageBuilderFooterItem,
  MenuPageBuilderFooterColumn,
  menuPageBuilderFooterSpacingDefaults,
} from 'src/shared/dbTypes/menuPageBuilder'
import shopInfos from 'template/infos'
import shopConfig from 'template/config'
import { hasOptionalAnalytics } from 'src/functions/analyticsConfig'
import {
  getExternalLinkRel,
  isAnchorOnlyHref,
  isExternalHref,
} from 'src/functions/linkPolicy'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({

    wrapper: {
      backgroundColor: 'var(--builder-footer-surface, #ffffff)',
      color: 'var(--builder-footer-text, inherit)',
      '& .MuiTypography-root, & .MuiListItemText-root, & .MuiListItemButton-root, & .MuiListItem-root': {
        color: 'inherit',
      },
    },

    grid: {
      display: 'grid',
      rowGap: theme.spacing(6),
      paddingTop: 'var(--builder-footer-infos-padding-top, 40px)',
      paddingBottom: 'var(--builder-footer-infos-padding-bottom, 40px)',
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      columnGap: theme.spacing(5),

      [theme.breakpoints.up(640)]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [theme.breakpoints.up(1000)]: {
        gridTemplateColumns: 'repeat(var(--builder-footer-columns, 4), minmax(0, 1fr))',
      },
    },

    column: {
      minWidth: 0,
    },

    mobileGrid: {
      [theme.breakpoints.down(640)]: {
        gap: 0,
        padding: theme.spacing(2, 0),
      },
    },

    accordion: {
      [theme.breakpoints.down(640)]: {
        borderTop: '1px solid currentColor',
        '&:last-child': { borderBottom: '1px solid currentColor' },
        '&[open] > summary > span::after': { content: '"−"' },
        '& > div': { paddingBottom: theme.spacing(2) },
      },
    },

    accordionSummary: {
      display: 'none',
      [theme.breakpoints.down(640)]: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: theme.spacing(2), minHeight: 52, padding: theme.spacing(1, 0),
        cursor: 'pointer', fontWeight: 600, listStyle: 'none',
        '&::-webkit-details-marker': { display: 'none' },
        '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 3 },
      },
    },

    accordionIndicator: {
      '&::after': { content: '"+"', fontSize: '1.3rem', fontWeight: 400 },
    },

    desktopColumnTitle: {
      [theme.breakpoints.down(640)]: { display: 'none' },
    },

    mobileEssentials: {
      display: 'none',
      [theme.breakpoints.down(640)]: {
        display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0, 2),
        paddingBottom: theme.spacing(2), fontSize: '.8rem',
        '& a, & button': {
          display: 'inline-flex', alignItems: 'center', minHeight: 44,
          color: 'inherit', textUnderlineOffset: 3,
        },
        '& button': {
          border: 0, padding: 0, background: 'none', font: 'inherit',
          textDecoration: 'underline', cursor: 'pointer',
        },
        '& a:focus-visible, & button:focus-visible': { outline: '2px solid currentColor', outlineOffset: 3 },
      },
    },

    list: {},

    iconList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(.75),
      paddingTop: theme.spacing(1),
    },

    iconButton: {
      width: 34,
      minWidth: 34,
      height: 34,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      color: 'inherit',
      backgroundColor: 'rgba(255,255,255,.48)',
      border: '1px solid rgba(15, 23, 42, 0.12)',
      transition: 'background-color 160ms ease, transform 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,.78)',
        transform: 'translateY(-1px)',
      },
      '& svg': {
        fontSize: 19,
      },
    },

    itemImage: {
      maxHeight: 34,
      width: 'auto',
      maxWidth: 120,
      objectFit: 'contain',
      display: 'block',
    },
    itemImageWrap: {
      display: 'inline-block',
      lineHeight: 0,
      position: 'relative',
    },

    htmlText: {
      color: 'inherit',
      '& p': {
        margin: 0,
      },
      '& p:not(:last-child)': {
        marginBottom: theme.spacing(1.5),
      },
      '& a': {
        color: 'inherit',
      },
    },

    cookieSettingsButton: {
      appearance: 'none',
      border: 0,
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
      textAlign: 'left',
      textTransform: 'none',
      width: '100%',
    },
  })
)

const cookieSettingsHref = '#cookie-einstellungen'
const cookieSettingsItem: MenuPageBuilderFooterItem = {
  label: 'Cookie-Einstellungen',
  href: cookieSettingsHref,
}

const essentialFooterPaths = new Set([
  '/kontakt', '/zahlung-und-versand', '/impressum', '/agb',
  '/datenschutzerklaerung', '/widerrufsbelehrung', '/widerruf', cookieSettingsHref,
])

// Reuse configured labels and destinations; do not replace or remove CMS items.
const getEssentialFooterItems = (columns: MenuPageBuilderFooterColumn[]) => {
  const seen = new Set<string>()
  return columns.flatMap(column => column.items || []).filter(item => {
    const href = item.href
    if (!href || seen.has(href)
      || (!essentialFooterPaths.has(href) && !/^(mailto:|tel:)/i.test(href))) return false
    seen.add(href)
    return true
  })
}

const getNormalizedFooterText = (value?: string) => (value || '').toLocaleLowerCase('de-DE')

const isCookieSettingsItem = (item: MenuPageBuilderFooterItem) =>
  item.href === cookieSettingsHref || item.label === cookieSettingsItem.label

const isCustomerServiceColumn = (column: MenuPageBuilderFooterColumn) =>
  getNormalizedFooterText(column.title).includes('kundenservice')

const isCallbackFooterItem = (item: MenuPageBuilderFooterItem) => {
  const label = getNormalizedFooterText(item.label)
  const href = getNormalizedFooterText(item.href)

  return label.includes('rückruf')
    || label.includes('rueckruf')
    || href.includes('rueckruf')
    || href.includes('callback')
}

const isAboutPageItem = (item: MenuPageBuilderFooterItem) =>
  item.href === '/ueber-uns' || getNormalizedFooterText(item.label) === 'über uns'

const showAboutPageLink = (shopConfig as typeof shopConfig & {
  showAboutPageLink?: boolean
}).showAboutPageLink !== false

const withCookieSettingsItem = (columns: MenuPageBuilderFooterColumn[]) => {
  if (!hasOptionalAnalytics) return columns
  if (columns.some(column => column.items?.some(isCookieSettingsItem))) {
    return columns
  }

  const customerServiceIndex = columns.findIndex(isCustomerServiceColumn)
  const datenschutzIndex = columns.findIndex(column =>
    column.items?.some(item => item.href === '/datenschutzerklaerung' || item.label === 'Datenschutz')
  )
  const fallbackIndex = columns.findIndex(column => column.items?.some(item => !item.icon && !item.imageSrc))
  const targetIndex = [customerServiceIndex, datenschutzIndex, fallbackIndex, 0].find(index => index >= 0) || 0

  return columns.map((column, index) => {
    if (index !== targetIndex) return column

    const items = column.items || []
    const nextItems = [...items]
    const callbackIndex = items.findIndex(isCallbackFooterItem)

    if (callbackIndex >= 0) {
      nextItems.splice(callbackIndex, 1, cookieSettingsItem)
    } else {
      const privacyIndex = items.findIndex(item => item.href === '/datenschutzerklaerung' || item.label === 'Datenschutz')
      nextItems.splice(privacyIndex >= 0 ? privacyIndex + 1 : nextItems.length, 0, cookieSettingsItem)
    }

    return {
      ...column,
      items: nextItems,
    }
  })
}

const getFooterIcon = (icon?: string) => {
  switch (icon) {
    case 'instagram':
      return <InstagramIcon fontSize='small' />
    case 'pinterest':
      return <PinterestIcon fontSize='small' />
    case 'facebook':
      return <FacebookIcon fontSize='small' />
    case 'youtube':
      return <YouTubeIcon fontSize='small' />
    default:
      return null
  }
}

interface Props {
  backgroundColor?: string
  textColor?: string
  columns?: MenuPageBuilderFooterColumn[]
  columnsPerRow?: number
  accessibleLinks?: boolean
  mobileAccordions?: boolean
  paddingTop?: number
  paddingBottom?: number
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const InfosUndLinks: React.FC<Props> = ({
  backgroundColor,
  textColor,
  columns,
  columnsPerRow = 4,
  mobileAccordions = false,
  accessibleLinks = false,
  paddingTop,
  paddingBottom,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  // Keep SSR and the first hydrated render identical and fully readable. The
  // native groups become compact only after the client knows the viewport.
  const desktopColumns = useMediaQuery('(min-width: 640px)', { defaultMatches: true })
  const [expandedColumns, setExpandedColumns] = useState<Record<string, boolean>>({})
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated],
  )
  const wrapperStyle = {
    ['--builder-footer-surface' as string]: backgroundColor || undefined,
    ['--builder-footer-text' as string]: textColor || undefined,
    ['--builder-footer-columns' as string]: String(columnsPerRow || 4),
    ['--builder-footer-infos-padding-top' as string]: `${paddingTop ?? menuPageBuilderFooterSpacingDefaults.infosPaddingTop}px`,
    ['--builder-footer-infos-padding-bottom' as string]: `${paddingBottom ?? menuPageBuilderFooterSpacingDefaults.infosPaddingBottom}px`,
  } as CSSProperties

  const safeColumnsWithDefault: MenuPageBuilderFooterColumn[] = columns && columns.length > 0
    ? columns
    : [
        {
          id: 'footer-brand',
          title: shopInfos.name,
          items: [
            { label: 'Zahlung & Versand', href: '/zahlung-und-versand' },
            { label: 'Auslandsversandkosten', href: '/auslandsversandkosten' },
            { label: 'Impressum', href: '/impressum' },
            { label: 'AGB', href: '/agb' },
            { label: 'Widerrufsrecht', href: '/widerrufsbelehrung' },
            { label: 'Vertrag widerrufen', href: '/widerruf' },
            { label: 'Datenschutz', href: '/datenschutzerklaerung' },
          ],
        },
        {
          id: 'footer-contact',
          title: 'Kontakt',
          items: [
            { label: `Mail.: ${shopInfos.email}`, href: `mailto:${shopInfos.email}` },
            { label: 'Kontaktformular', href: '/kontakt' },
            { label: 'Mo - Fr. 09:00 bis 18:00 Uhr' },
          ],
        },
        {
          id: 'footer-payment',
          title: 'Zahlungsarten',
          items: [
            { label: 'PayPal' },
            { label: 'SEPA Lastschrift' },
            { label: 'Kreditkarte' },
          ],
        },
        {
          id: 'footer-shipping',
          title: 'Versandarten',
          items: [{ label: 'DHL für Pakete' }],
        },
      ]
  const safeColumns = showAboutPageLink
    ? safeColumnsWithDefault
    : safeColumnsWithDefault.map(column => ({
        ...column,
        items: column.items?.filter(item => !isAboutPageItem(item)),
      }))
  const renderedColumns = withCookieSettingsItem(safeColumns)
  const essentialItems = mobileAccordions ? getEssentialFooterItems(renderedColumns) : []

  return <div className={classes.wrapper} style={wrapperStyle}>
    <Container maxWidth='lg'>
      <div className={[classes.grid, mobileAccordions ? classes.mobileGrid : ''].join(' ')} data-footer-mobile-accordions={mobileAccordions || undefined}>
        {renderedColumns.map((column, columnIndex) => {
          const columnKey = column.id || `${column.title}-${columnIndex}`
          const content = <div>
            <Typo component='div' variant='h6' gutterBottom className={mobileAccordions ? classes.desktopColumnTitle : undefined}>
              {column.title}
            </Typo>

            {column.bodyHtml &&
              <div className={classes.htmlText}>
                {parse(column.bodyHtml)}
              </div>
            }

            {column.items && column.items.length > 0 &&
              <List className={[classes.list, column.items.some(item => item.icon) ? classes.iconList : ''].join(' ')}>
                {column.items.map((item, index) => {
                  const icon = getFooterIcon(item.icon)
                  const content = icon
                    ? icon
                    : item.imageSrc
                      ? <span className={classes.itemImageWrap}>
                          <img
                            src={item.imageSrc}
                            alt={item.imageAlt || item.label}
                            className={classes.itemImage}
                            loading='lazy'
                          />
                          <AiGeneratedBadge
                            aiGenerated={isAiGeneratedLandingImage(item.imageSrc)}
                            size='micro'
                            placement='top-left'
                          />
                        </span>
                      : <ListItemText primary={item.label} />

                  if (item.href) {
                    if (item.href === cookieSettingsHref) {
                      return <ListItemButton
                        key={`${column.id || column.title}-${index}`}
                        component='button'
                        type='button'
                        className={classes.cookieSettingsButton}
                        onClick={() => dispatch({ type: 'showCookieBanner' })}
                      >
                        {content}
                      </ListItemButton>
                    }

                    if (isAnchorOnlyHref(item.href)) {
                      return <ListItemButton
                        key={`${column.id || column.title}-${index}`}
                        component='a'
                        href={item.href}
                        target={isExternalHref(item.href) ? '_blank' : undefined}
                        rel={getExternalLinkRel(item.href, item.externalRel)}
                        className={icon ? classes.iconButton : undefined}
                        aria-label={icon ? item.label : undefined}
                        title={icon ? item.label : undefined}
                      >
                        {content}
                      </ListItemButton>
                    }

                    return <Link key={`${column.id || column.title}-${index}`} href={item.href} passHref legacyBehavior>
                      <ListItemButton
                        component={mobileAccordions || accessibleLinks ? 'a' : 'li'}
                        className={icon ? classes.iconButton : undefined}
                        aria-label={icon ? item.label : undefined}
                        title={icon ? item.label : undefined}
                      >
                        {content}
                      </ListItemButton>
                    </Link>
                  }

                  return <ListItem
                    key={`${column.id || column.title}-${index}`}
                    className={icon ? classes.iconButton : undefined}
                    aria-label={icon ? item.label : undefined}
                    title={icon ? item.label : undefined}
                  >
                    {content}
                  </ListItem>
                })}
              </List>
            }
          </div>

          return mobileAccordions
            ? <details key={columnKey} className={`${classes.column} ${classes.accordion}`} open={desktopColumns || Boolean(expandedColumns[columnKey])}
                onToggle={event => {
                  if (desktopColumns) return
                  const open = event.currentTarget.open
                  setExpandedColumns(current => current[columnKey] === open ? current : { ...current, [columnKey]: open })
                }}>
                <summary className={classes.accordionSummary}>{column.title}<span className={classes.accordionIndicator} aria-hidden='true' /></summary>
                {content}
              </details>
            : <div key={columnKey} className={classes.column}>{content}</div>
        })}
      </div>
      {essentialItems.length > 0 && <nav className={classes.mobileEssentials} aria-label='Kontakt und rechtliche Informationen'>
        {essentialItems.map(item => item.href === cookieSettingsHref
          ? <button key={item.href} type='button' onClick={() => dispatch({ type: 'showCookieBanner' })}>{item.label}</button>
          : <Link key={item.href} href={item.href || '#'}>{item.label}</Link>)}
      </nav>}
    </Container>
  </div>
}

export default InfosUndLinks
